import { getPlatformId, getRegistryUrl } from './compass-services';
import { readCookie } from './cookies-services';

const AMDLOADER_SCRIPT = 'AmdLoader/AmdLoader.js';

let initAsync: Promise<unknown> | undefined;

// eslint-disable-next-line max-statements
const setupGlobalEnvVar = async () => {
  if (typeof window === 'undefined') {
    return;
  }

  const tenant = getPlatformId();

  window.ds = {
    anonymous: false,
    tenant,
    registryUrl: getRegistryUrl(),
  };

  window.dsPrefix = 'uwp/';
  window.dsLang = readCookie('swymlang') ?? 'en';
  window.dsVersion = '';

  if (import.meta.env.DEV) {
    window.dsBaseUrl = `${window.location.origin}/`;
    window.dsResourceUrl = `${window.location.origin}/`;
  } else {
    window.dsBaseUrl = `${window.location.origin}/`;
    window.dsResourceUrl = `${window.location.origin}/`;
  }

  window.dsUserGranted = true;
  window.dsCsrfOptions = { protection: false };
  window.dsUntrustedBaseUrl = '';
  window.dsDefaultWebappsBaseUrl = `${window.dsResourceUrl}webapps/`;
  window.dsDefaultLoadedBundles = ['wp', 'V6frame', 'swym'];
  window.dsPlatformPrefixUrlEnabled = true;

  // Bundles configurations
  window.bundles = {
    V6frame: ['wp'],
    wp: [],
    swym: ['V6frame'],
  };

  // Widget App Platform global variables
  window.wap = {
    widgetInstancesCacheId: 'F88j1',
  };

  // UWA configurations
  window.UWA = {
    ...window.UWA,
    hosts: {
      uwa: `${window.dsResourceUrl + window.dsPrefix}resources/0/en/wp`,
      exposition: `${window.dsBaseUrl + window.dsPrefix}api`,
      untrustedExposition: `${window.dsUntrustedBaseUrl + window.dsPrefix}api`,
      proxies: {
        passport: `${window.dsBaseUrl + window.dsPrefix}api/proxy/passport`,
      },
    },
  };

  // To Tell MPF witch env we are
  window.$nuxt = {
    $env: {
      PLATFORM_ID: tenant,
    },
  };

  const baseUrl = window.dsDefaultWebappsBaseUrl.slice(0, -1); // Remove last "/" in base URL

  // Require configurations
  window.require = {
    urlArgs: 'bust=' + (import.meta.env.VITE_BUILD_ID as string),
    baseUrl,
    paths: {
      DS: baseUrl,
      ds: `${window.dsResourceUrl}resources/0/fr/wp/script`,
      'DS/UWPClientCode/UWA': 'UWA2/js',
      UWA: 'UWA2/js',
      vendors: 'vendors',

      // We need to import specific highcharts & modules files directly from
      // the prereqs instead of the whole package to avoid using HighchartsLoader.js that has issues
      highcharts: `${baseUrl}/VENHighcharts/highcharts.7.1.2`,
      'highcharts-modules': `${baseUrl}VENHighcharts/highcharts.7.1.2/modules`,

      vuejs: `${baseUrl}/vuejs/2.6.10/vue.min`,
      'vu-kit': `${baseUrl}/vuekit/vu-kit.umd.min`, // Have to call it "vu-kit"
      'vu-kit-css': `${baseUrl}/vuekit/vu-kit`,
      vuex: `${baseUrl}/vuex/3.1.1/vuex.min`,
    },
    packages: [
      {
        name: 'highcharts',
        main: 'highcharts',
      },
    ],
    config: {
      i18n: {
        locale: readCookie('swymlang') ?? 'en',
      },
      'DS/Logger/Logger': {
        disableLog: !false,
      },
    },
    skipDataMain: true,
  };
};

const unbindModules = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.define('css!DS/Core/wux', [], () => '');
  window.define('css!UIKIT/UIKIT', [], () => '');
  window.define('css!DS/i3DXCompass/i3DXCompass', [], () => '');
};

const loadRealAMDFromScript = async () => {
  if (typeof window === 'undefined') {
    return;
  }

  const script = document.createElement('script');

  const loadingAsync = new Promise((resolve, reject) => {
    script.onload = resolve;
    script.onerror = reject;
  });

  script.src = `${window.dsDefaultWebappsBaseUrl}${AMDLOADER_SCRIPT}`;
  document.head.appendChild(script); // Or something of the likes

  await loadingAsync;
};

const initRequire = async () => {
  if (initAsync) {
    return;
  }

  await setupGlobalEnvVar();
  await loadRealAMDFromScript();

  // Debbind files
  unbindModules();
};

// DS platform modules are loaded through the browser-only AMD loader. During SSR there
// is no `window.require`, so the real loader can never run. Returning inert stand-ins
// (instead of a promise that never settles) prevents a top-level `await requirejs(...)`
// in a component's <script setup> from hanging `renderToString` forever — which would
// leave the SSR document pending until it times out. The client re-runs setup with the
// real modules on hydration.
const createSSRStub = (): unknown =>
  new Proxy(function () {}, {
    get: (_target, prop) => (prop === Symbol.toPrimitive ? () => '' : createSSRStub()),
    apply: () => createSSRStub(),
    construct: () => createSSRStub(),
  });

export const requirejs = async <T>(modules: string[]): Promise<T> => {
  if (typeof window === 'undefined') {
    return modules.map(() => createSSRStub()) as unknown as T;
  }

  if (!initAsync) {
    initAsync = initRequire();
  }

  await initAsync;

  return new Promise((resolve, reject) => {
    window.require(
      modules,
      (...m) => {
        resolve(m);
      },
      reject,
    );
  });
};

export const isDefined = async (module: string): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!initAsync) {
    initAsync = initRequire();
  }

  await initAsync;
  return window.require.defined(module);
};
